import { useEffect, useMemo, useState } from "react";
import { Activity, AlertTriangle, CheckCircle2, ClipboardList, Eye, Loader2, Mail, MessageSquare, Rocket, Route, ShieldAlert, Users } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow, subDays, subHours } from "date-fns";

type PageEvent = { path: string; session_id: string; user_id: string | null; org_id: string | null; created_at: string };

type OpsData = {
  events: PageEvent[];
  campaigns: Array<{ id: string; status: string; org_id: string }>;
  enrollments: Array<{ campaign_id: string; status: string; next_send_at: string | null; unsubscribed_at: string | null }>;
  profilesWithoutOrg: number;
  orgs: number;
  jobs: number;
  leads: number;
  estimates: number;
  bridgetteFiles: number;
  aiErrors24h: number;
  queueFailed24h: number;
  gmailConnectedOrgs: number;
  smsConnectedOrgs: number;
  campaignEmailFailures24h: number;
  campaignSmsFailures24h: number;
  campaignEmailVolume7d: number;
  campaignSmsVolume7d: number;
  recentUnsubscribes: number;
};

const count = async (table: string, apply?: (q: any) => any) => {
  const q = (supabase as any).from(table).select("id", { count: "exact", head: true });
  const { count, error } = await (apply ? apply(q) : q);
  if (error) return 0;
  return count || 0;
};

export function PlatformOperationsDashboard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<OpsData | null>(null);

  useEffect(() => {
    const load = async () => {
      const last24h = subHours(new Date(), 24).toISOString();
      const last7d = subDays(new Date(), 7).toISOString();

      const [{ data: events }, { data: campaigns }, { data: enrollments }, profilesWithoutOrg, orgs, jobs, leads, estimates, bridgetteFiles, aiErrors24h, queueFailed24h, gmailConnectedOrgs, smsConnectedOrgs, campaignEmailFailures24h, campaignSmsFailures24h, campaignEmailVolume7d, campaignSmsVolume7d, recentUnsubscribes] = await Promise.all([
        (supabase as any).from("platform_page_events").select("path, session_id, user_id, org_id, created_at").gte("created_at", last7d).order("created_at", { ascending: false }).limit(1000),
        supabase.from("ai_campaigns").select("id, status, org_id"),
        supabase.from("campaign_enrollments").select("campaign_id, status, next_send_at, unsubscribed_at"),
        count("profiles", (q) => q.is("org_id", null)),
        count("organizations"),
        count("jobs"),
        count("leads"),
        count("job_estimates"),
        count("bridgette_workspace_files"),
        count("ai_usage_logs", (q) => q.gte("created_at", last24h).eq("status", "error")),
        count("analysis_job_queue", (q) => q.gte("created_at", last24h).eq("status", "failed")),
        count("gmail_tokens"),
        count("organizations", (q) => q.eq("sms_enabled", true).not("sms_from_number", "is", null)),
        count("campaign_email_sends", (q) => q.gte("created_at", last24h).in("status", ["failed", "bounced"])),
        count("campaign_sms_sends", (q) => q.gte("created_at", last24h).eq("status", "failed")),
        count("campaign_email_sends", (q) => q.gte("created_at", last7d).eq("status", "sent")),
        count("campaign_sms_sends", (q) => q.gte("created_at", last7d).eq("status", "sent")),
        count("campaign_enrollments", (q) => q.gte("unsubscribed_at", last7d)),
      ]);

      setData({ events: events || [], campaigns: campaigns || [], enrollments: enrollments || [], profilesWithoutOrg, orgs, jobs, leads, estimates, bridgetteFiles, aiErrors24h, queueFailed24h, gmailConnectedOrgs, smsConnectedOrgs, campaignEmailFailures24h, campaignSmsFailures24h, campaignEmailVolume7d, campaignSmsVolume7d, recentUnsubscribes });
      setLoading(false);
    };

    void load();
  }, []);

  const stats = useMemo(() => {
    const events = data?.events || [];
    const last24 = subHours(new Date(), 24).getTime();
    const events24h = events.filter((event) => new Date(event.created_at).getTime() >= last24);
    const sessions = new Set(events.map((event) => event.session_id)).size;
    const users = new Set(events.map((event) => event.user_id).filter(Boolean)).size;
    const stuckApproved = (data?.campaigns || []).filter((campaign) => campaign.status === "approved" && !(data?.enrollments || []).some((enrollment) => enrollment.campaign_id === campaign.id)).length;
    const stuckEnrollments = (data?.enrollments || []).filter((enrollment) => enrollment.status === "active" && !enrollment.next_send_at).length;
    const topPaths = Array.from(events.reduce((map, event) => map.set(event.path, (map.get(event.path) || 0) + 1), new Map<string, number>()))
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
    return { events24h: events24h.length, sessions, users, stuckApproved, stuckEnrollments, topPaths };
  }, [data?.campaigns, data?.enrollments, data?.events]);

  if (loading || !data) {
    return <Card><CardContent className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></CardContent></Card>;
  }

  const checklist = [
    { label: "Single admin surface", detail: "/admin now redirects into Platform Admin.", ok: true },
    { label: "Traffic tracking", detail: `${stats.events24h} page views in the last 24 hours, ${stats.sessions} sessions in the last 7 days.`, ok: data.events.length > 0 },
    { label: "CRM records flowing", detail: `${data.jobs} jobs, ${data.leads} leads, ${data.estimates} estimates currently in the CRM.`, ok: data.jobs + data.leads > 0 },
    { label: "Bridgette document pipeline", detail: `${data.bridgetteFiles} workspace files indexed.`, ok: data.bridgetteFiles > 0 },
    { label: "User onboarding health", detail: `${data.profilesWithoutOrg} users are missing an organization.`, ok: data.profilesWithoutOrg === 0 },
    { label: "AI/queue stability", detail: `${data.aiErrors24h} AI errors and ${data.queueFailed24h} failed queued jobs in 24h.`, ok: data.aiErrors24h === 0 && data.queueFailed24h === 0 },
    { label: "Sales Hub launch path", detail: `${data.campaigns.length} campaigns, ${data.enrollments.length} enrollments, ${stats.stuckApproved} approved with no enrollments.`, ok: data.campaigns.length > 0 && stats.stuckApproved === 0 },
    { label: "Marketing delivery health", detail: `${data.campaignEmailFailures24h + data.campaignSmsFailures24h} send failures in 24h, ${stats.stuckEnrollments} active enrollments with no next send.`, ok: data.campaignEmailFailures24h + data.campaignSmsFailures24h === 0 && stats.stuckEnrollments === 0 },
    { label: "Email/SMS readiness", detail: `${data.gmailConnectedOrgs} orgs with Gmail, ${data.smsConnectedOrgs} orgs with SMS numbers, ${data.recentUnsubscribes} unsubscribes in 7d.`, ok: data.gmailConnectedOrgs > 0 },
  ];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <Card><CardHeader className="pb-2"><CardDescription className="flex items-center gap-2"><Eye className="h-4 w-4" /> Page views</CardDescription><CardTitle>{stats.events24h}</CardTitle></CardHeader><CardContent className="text-sm text-muted-foreground">Last 24 hours</CardContent></Card>
        <Card><CardHeader className="pb-2"><CardDescription className="flex items-center gap-2"><Activity className="h-4 w-4" /> Sessions</CardDescription><CardTitle>{stats.sessions}</CardTitle></CardHeader><CardContent className="text-sm text-muted-foreground">Last 7 days</CardContent></Card>
        <Card><CardHeader className="pb-2"><CardDescription className="flex items-center gap-2"><Users className="h-4 w-4" /> Active users</CardDescription><CardTitle>{stats.users}</CardTitle></CardHeader><CardContent className="text-sm text-muted-foreground">Tracked users</CardContent></Card>
        <Card><CardHeader className="pb-2"><CardDescription className="flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> Open issues</CardDescription><CardTitle>{checklist.filter((item) => !item.ok).length}</CardTitle></CardHeader><CardContent className="text-sm text-muted-foreground">Production checklist</CardContent></Card>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card><CardHeader className="pb-2"><CardDescription className="flex items-center gap-2"><Rocket className="h-4 w-4" /> Campaigns</CardDescription><CardTitle>{data.campaigns.length}</CardTitle></CardHeader><CardContent className="text-sm text-muted-foreground">{stats.stuckApproved} approved without enrollments</CardContent></Card>
        <Card><CardHeader className="pb-2"><CardDescription className="flex items-center gap-2"><Mail className="h-4 w-4" /> Email volume</CardDescription><CardTitle>{data.campaignEmailVolume7d}</CardTitle></CardHeader><CardContent className="text-sm text-muted-foreground">{data.campaignEmailFailures24h} failures in 24h</CardContent></Card>
        <Card><CardHeader className="pb-2"><CardDescription className="flex items-center gap-2"><MessageSquare className="h-4 w-4" /> SMS volume</CardDescription><CardTitle>{data.campaignSmsVolume7d}</CardTitle></CardHeader><CardContent className="text-sm text-muted-foreground">{data.campaignSmsFailures24h} failures in 24h</CardContent></Card>
        <Card><CardHeader className="pb-2"><CardDescription className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Stuck sends</CardDescription><CardTitle>{stats.stuckEnrollments}</CardTitle></CardHeader><CardContent className="text-sm text-muted-foreground">Active enrollments missing next send</CardContent></Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><ClipboardList className="h-5 w-5 text-primary" /> Production readiness checklist</CardTitle><CardDescription>What still needs attention before calling the CRM production-ready.</CardDescription></CardHeader>
          <CardContent className="space-y-3">
            {checklist.map((item) => (
              <div key={item.label} className="flex items-start gap-3 rounded-lg border border-border bg-background p-3">
                {item.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />}
                <div className="min-w-0 flex-1"><div className="font-medium text-foreground">{item.label}</div><p className="mt-1 text-sm text-muted-foreground">{item.detail}</p></div>
                <Badge variant={item.ok ? "outline" : "destructive"}>{item.ok ? "OK" : "Fix"}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Route className="h-5 w-5 text-primary" /> Top traffic paths</CardTitle><CardDescription>Most-viewed routes from tracked app activity.</CardDescription></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow><TableHead>Path</TableHead><TableHead className="text-right">Views</TableHead></TableRow></TableHeader>
              <TableBody>
                {stats.topPaths.map(([path, views]) => <TableRow key={path}><TableCell className="max-w-[260px] truncate">{path}</TableCell><TableCell className="text-right">{views}</TableCell></TableRow>)}
                {stats.topPaths.length === 0 ? <TableRow><TableCell colSpan={2} className="py-8 text-center text-muted-foreground">Traffic will appear after users navigate the app.</TableCell></TableRow> : null}
              </TableBody>
            </Table>
            {data.events[0] ? <p className="mt-3 text-xs text-muted-foreground">Latest event {formatDistanceToNow(new Date(data.events[0].created_at), { addSuffix: true })}</p> : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}